import isElectron from 'is-electron';

import createTopic from '@paradigm/utils/createTopic';
import getInitials from '@paradigm/utils/src/getInitials';
import { exceptionLogger } from '@paradigm/logging/src/logging';

import notificationAudioWin from '#/assets/notification_win.ogg';
import notificationAudioOsx from '#/assets/notification_osx.ogg';

import logoSrc from '#/assets/logo.png';

import {
  getAnonymousImage,
  getUserImage,
} from '#/utils/image-generator/base64Image';

import { COLORS } from '#/components/room-item-avatar';

let sound;
if (window.navigator.platform.includes('Mac')) {
  sound = new Audio(notificationAudioOsx);
} else if (window.navigator.platform.includes('Win')) {
  sound = new Audio(notificationAudioWin);
}

/**
 * Maximum length for notification message.
 * See https://electronjs.org/docs/tutorial/notifications
 */
const NOTIFICATION_MESSAGE_LIMIT = 195;

/**
 * Prepares message to be used in a notification
 * @param {string} text
 */
function sanitizeMessage(text) {
  return text.length > NOTIFICATION_MESSAGE_LIMIT
    ? `${text.slice(0, NOTIFICATION_MESSAGE_LIMIT)}...`
    : text;
}

/**
 * Whether or not the platform allows to display a secondary icon,
 * which is suitable for displaying an icon identifying the agent,
 * of the action that the current user is being notified about.
 * The primary icon slot is always used to display Paradigm's logo.
 */
const isSecondaryIconSupported =
  isElectron() && window.navigator.platform.includes('Mac');

/** @typedef {"anonymous" | { readonly firm: string; readonly ticker: string; readonly color: string }} Agent */

/**
 * @param {object} param
 * @param {Agent} param.agent Used to generate title and icon, when supported
 * @param {string} param.message
 * @param {boolean} param.silent
 * @param {(() => void) | null} [param.onClick]
 */
export function fireAgentNotification({
  agent,
  message,
  silent,
  onClick = null,
}) {
  const title = makeAgentTitle(agent);

  const icon = () =>
    isSecondaryIconSupported ? makeAgentIcon(agent) : logoSrc;

  fireNotification({ title, message, silent, icon, onClick });
}

/**
 * @param {object} param
 * @param {string} param.notificationId
 * @param {string} param.title
 * @param {string | null} param.message
 * @param {string | () => string} [param.icon]
 * @param {boolean} param.silent
 * @param {(() => void) | null} [param.onClick]
 */
export function fireNotification({
  notificationId,
  title,
  message,
  icon = logoSrc,
  silent,
  onClick = null,
}) {
  (async () => {
    if (typeof Notification === 'undefined') return;

    if (Notification.permission !== 'granted')
      await Notification.requestPermission();

    // Do not rely on the OS to play the notification sound.
    // Play sound regardless of the permission given to the platform.
    if (!silent) playNotificationSound();

    if (Notification.permission !== 'granted') return;

    const notification = new Notification(title, {
      body: message != null ? sanitizeMessage(message) : '',
      icon: typeof icon === 'function' ? icon() : icon,
      silent: true,
      tag: notificationId,
    });
    notification.onclick = onClick;
  })().catch(exceptionLogger('Error firing notification.'));
}

export function playNotificationSound() {
  sound?.play().catch(() => {
    /* do nothing */
  });
}

/**
 * @param {Agent} agent
 */
function makeAgentIcon(agent) {
  return agent === 'anonymous'
    ? getAnonymousImage()
    : getUserImage(getInitials(agent.firm), COLORS[agent.color]);
}

/**
 * Generates a title like `"Firm Name (TICKER)"` based on
 * `firm` and `ticker` of the agent.
 * @param {Agent} agent
 */
function makeAgentTitle(agent) {
  return agent === 'anonymous'
    ? 'Anonymous'
    : `${agent.firm} (${agent.ticker})`;
}

/** @typedef {{ roomId?: number; messageId?: number }} ClickPayload */

/** @type {import("@paradigm/utils/createTopic").Topic<ClickPayload>} */
export const clickedTopic = createTopic('notification-clicked');

/**
 * @param {ClickPayload} payload
 */
export function createClickHandler(payload) {
  /**
   * @this {Notification}
   * @param {Event} event
   */
  return function handleNotificationClick() {
    clickedTopic.publish(payload);
  };
}
